import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  CircularProgress,
  TextField,
  Stack,
  Rating,
  Tabs,
  Tab,
  Chip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarIcon from "@mui/icons-material/Star";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InputIcon from "@mui/icons-material/Input";
import ArticleIcon from "@mui/icons-material/Article";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderIcon from "@mui/icons-material/Folder";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useIntl } from "react-intl";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import {
  fetchWorkDetail,
  fetchAction,
  startWork,
  completeWork,
  fetchWorkGuidelines,
  fetchWorkInputDataCategories,
  fetchWorkOutputData,
  fetchWorkOutputDataCategories,
  createOutputData,
  updateOutputData,
  deleteOutputData,
  fetchDataItemsByDataType,
  fetchDataType,
  fetchEvaluations,
  createEvaluation,
  updateEvaluation,
  deleteEvaluation,
  evaluateWork,
  updateDoneWork,
  deleteDoneWork,
  updateTodoWork,
  deleteTodoWork,
  fetchAgentsByWork,
} from "../../api/memberApi";

// ── 言語設定に応じて姓名を結合 ──────────────────────────────────
const buildMemberName = (member) => {
  if (!member) return "";
  const last = member.lastName || "";
  const first = member.firstName || "";
  if (navigator.language === "ja") return `${last} ${first}`.trim();
  return `${first} ${last}`.trim();
};

// ── ラベル + 値の行 ──────────────────────────────────────────────
function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography
        variant="body2"
        sx={{ width: 140, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

// ── タブパネル ────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ── datetime-local 用フォーマット ────────────────────────────────
const toLocalDateTimeInput = (date) => {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── 経過時間計算 ─────────────────────────────────────────────────
const calcExpendedTime = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (ms < 0) return null;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, minutes };
};

// ── ステータスチップ ─────────────────────────────────────────────
function StatusChip({ status, intl }) {
  const map = {
    Todo: { label: intl.formatMessage({ id: "work.todo" }), color: "default" },
    Doing: { label: intl.formatMessage({ id: "work.doing" }), color: "primary" },
    Done: { label: intl.formatMessage({ id: "work.done" }), color: "success" },
    Evaluated: { label: intl.formatMessage({ id: "work.evaluated" }), color: "secondary" },
  };
  const cfg = map[status] || { label: status, color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// ── データ項目展開セクション ────────────────────────────────────
function DataItemsSection({ category, t }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemsByType, setItemsByType] = useState(null); // null = 未取得

  // associatedDataTypes が設定されていれば使用、
  // なければ dataTypeId（単体フィールド）をフォールバックとして使用
  const associatedTypes = (() => {
    if (category?.associatedDataTypes?.length > 0) return category.associatedDataTypes;
    if (category?.dataTypeId) {
      return [{ dataTypeId: category.dataTypeId, dataTypeName: category.dataTypeName || category.dataCategoryName }];
    }
    return [];
  })();
  if (associatedTypes.length === 0) return null;

  const handleChange = async (_, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && itemsByType === null) {
      setLoading(true);
      try {
        const results = await Promise.all(
          associatedTypes.map(async (dt) => {
            try {
              const [items, detail] = await Promise.all([
                fetchDataItemsByDataType(dt.dataTypeId),
                fetchDataType(dt.dataTypeId).catch(() => null),
              ]);
              return {
                dataTypeId: dt.dataTypeId, dataTypeName: dt.dataTypeName,
                items: Array.isArray(items) ? items : [],
                detail: detail || {},
              };
            } catch {
              return { dataTypeId: dt.dataTypeId, dataTypeName: dt.dataTypeName, items: [], detail: {} };
            }
          })
        );
        setItemsByType(results);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Accordion
      disableGutters elevation={0} expanded={expanded} onChange={handleChange}
      sx={{ mt: 1, border: "1px solid", borderColor: "divider", borderRadius: "4px !important", "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ minHeight: 32, px: 1.5, "& .MuiAccordionSummary-content": { my: 0.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ListAltIcon fontSize="small" color="action" />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {t("work.output.dataItems")}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (itemsByType || []).map(({ dataTypeId, dataTypeName, items, detail }) => (
          <Box key={dataTypeId} sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              {dataTypeName}
            </Typography>
            {(detail.dataOwner || detail.address || detail.dataSensitivityLevel || detail.dataRestrictedCategory) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 0.5, pl: 0.5 }}>
                {detail.dataOwner && (
                  <Typography variant="caption" color="text.secondary">
                    {t("work.output.dataItem.dataOwner")}: <strong>{detail.dataOwner}</strong>
                  </Typography>
                )}
                {detail.address && (
                  <Typography variant="caption" color="text.secondary">
                    {t("work.output.dataItem.dataOwnerMailAddress")}: <Link href={`mailto:${detail.address}`} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: "0.72rem" }}>{detail.address}</Link>
                  </Typography>
                )}
                {detail.dataSensitivityLevel && (
                  <Typography variant="caption" color="text.secondary">
                    {t("work.output.dataItem.dataSensitivityLevel")}: <strong>{detail.dataSensitivityLevel}</strong>
                  </Typography>
                )}
                {detail.dataRestrictedCategory && (
                  <Typography variant="caption" color="text.secondary">
                    {t("work.output.dataItem.dataRestrictedCategory")}: <strong>{detail.dataRestrictedCategory}</strong>
                  </Typography>
                )}
              </Box>
            )}
            {items.length === 0 ? (
              <Typography variant="caption" color="text.secondary">—</Typography>
            ) : (
              <Table size="small" sx={{ "& td, & th": { py: 0.3, px: 1, fontSize: "0.72rem" } }}>
                <TableHead>
                  <TableRow sx={{ "& th": { color: "text.secondary", fontWeight: 600 } }}>
                    <TableCell>{t("work.output.dataItem.name")}</TableCell>
                    <TableCell>{t("work.output.dataItem.domain")}</TableCell>
                    <TableCell align="center">PK</TableCell>
                    <TableCell align="center">FK</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <React.Fragment key={item.dataItemId}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                            {item.dataItemName}
                          </Typography>
                          {item.dataItemDescription && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {item.dataItemDescription}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.dataDomainName || "—"}</TableCell>
                        <TableCell align="center">{item.primaryKey ? "✔" : ""}</TableCell>
                        <TableCell align="center">{item.foreignKey ? "✔" : ""}</TableCell>
                      </TableRow>
                      {item.indicators?.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ pb: 1, pt: 0, pl: 2, borderBottom: "1px dashed", borderColor: "divider" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t("work.output.dataItem.kpi")}
                            </Typography>
                            <Table size="small" sx={{ mt: 0.5, "& td, & th": { py: 0.2, px: 0.8, fontSize: "0.7rem" } }}>
                              <TableHead>
                                <TableRow sx={{ "& th": { color: "text.secondary", fontWeight: 600 } }}>
                                  <TableCell>{t("work.output.dataItem.kpi.name")}</TableCell>
                                  <TableCell>{t("work.output.dataItem.kpi.unit")}</TableCell>
                                  <TableCell>{t("work.output.dataItem.kpi.measuringMethod")}</TableCell>
                                  <TableCell>{t("work.output.dataItem.kpi.evaluationStandard")}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {item.indicators.map((kpi) => (
                                  <TableRow key={kpi.indicatorName}>
                                    <TableCell>{kpi.indicatorName}</TableCell>
                                    <TableCell>{kpi.unit || "—"}</TableCell>
                                    <TableCell>{kpi.measuringMethod || "—"}</TableCell>
                                    <TableCell>{kpi.evaluationStandard || "—"}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

// ── アウトプットデータ追加/編集ダイアログ ──────────────────────
function OutputDataFormDialog({ open, onClose, onSave, editData, outputCategories, workId, companyId, intl }) {
  const t = (id) => intl.formatMessage({ id });
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dataName, setDataName] = useState("");
  const [dataDescription, setDataDescription] = useState("");
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    if (editData) {
      setSelectedCategory(editData.dataCategoryId || "");
      setDataName(editData.dataName || "");
      setDataDescription(editData.dataDescription || "");
      setDataUrl(editData.url || "");
    } else {
      setSelectedCategory("");
      setDataName("");
      setDataDescription("");
      setDataUrl("");
    }
  }, [editData, open]);

  const handleSave = () => {
    if (!dataName.trim()) return;
    const cat = outputCategories.find((c) => c.dataCategoryId === selectedCategory);
    onSave({
      ...(editData || {}),
      companyId,
      workId,
      dataName: dataName.trim(),
      dataCategoryId: selectedCategory || null,
      dataCategoryName: cat?.dataCategoryName || "",
      dataDescription: dataDescription.trim(),
      url: dataUrl.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
        {editData ? t("work.output.edit.title") : t("work.output.add.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {outputCategories.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>{t("work.output.dataCategory")}</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label={t("work.output.dataCategory")}
              >
                <MenuItem value="">{t("work.output.dataCategory.none")}</MenuItem>
                {outputCategories.map((cat) => (
                  <MenuItem key={cat.dataCategoryId} value={cat.dataCategoryId}>
                    {cat.dataCategoryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label={t("work.output.dataName")}
            value={dataName}
            onChange={(e) => setDataName(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t("work.output.dataDescription")}
            value={dataDescription}
            onChange={(e) => setDataDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            label="URL"
            value={dataUrl}
            onChange={(e) => setDataUrl(e.target.value)}
            size="small"
            fullWidth
            placeholder="https://..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={handleSave} disabled={!dataName.trim()}>
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── メインコンポーネント ─────────────────────────────────────────
export default function WorkDetailPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { workerId, workflowId, workId } = useParams();
  const { companyId, memberId, member } = useAuth();
  const notify = useNotification();

  const passedStatus = location.state?.status || "Todo";
  const passedWork = location.state?.work || null;

  const [work, setWork] = useState(passedWork);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // タブ状態（Done の場合はアウトプットタブを初期表示）
  const [tabIndex, setTabIndex] = useState(passedStatus === "Done" ? 2 : 0);

  // ガイドライン
  const [guidelines, setGuidelines] = useState([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);

  // インプットデータカテゴリ
  const [inputCategories, setInputCategories] = useState([]);
  const [inputLoading, setInputLoading] = useState(false);

  // アウトプットデータ
  const [outputData, setOutputData] = useState([]);
  const [outputLoading, setOutputLoading] = useState(false);
  const [outputCategories, setOutputCategories] = useState([]);
  const [outputDialogOpen, setOutputDialogOpen] = useState(false);
  const [editingOutput, setEditingOutput] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // ToDo 編集・削除
  const [todoEditMode, setTodoEditMode] = useState(false);
  const [editTodoName, setEditTodoName] = useState("");
  const [editTodoDescription, setEditTodoDescription] = useState("");
  const [savingTodo, setSavingTodo] = useState(false);
  const [deleteTodoConfirmOpen, setDeleteTodoConfirmOpen] = useState(false);
  const [deletingTodo, setDeletingTodo] = useState(false);

  // ToDo → Doing
  const [startDate, setStartDate] = useState(toLocalDateTimeInput(new Date()));

  // Doing → Done（endDate は完了ボタン押下時に確定するため初期値は空）
  const [endDate, setEndDate] = useState("");
  const [workResult, setWorkResult] = useState("");

  // 評価一覧
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationsLoading, setEvaluationsLoading] = useState(false);
  // 評価フォームダイアログ
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [evalEditTarget, setEvalEditTarget] = useState(null); // null=新規, obj=編集
  const [evalName, setEvalName] = useState("");
  const [evalDescription, setEvalDescription] = useState("");
  const [evalScore, setEvalScore] = useState(3);
  // 評価削除確認
  const [evalDeleteId, setEvalDeleteId] = useState(null);
  // 評価完了処理中
  const [completing, setCompleting] = useState(false);

  // 完了作業編集ダイアログ
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editDoneWorkName, setEditDoneWorkName] = useState("");
  const [editDoneDescription, setEditDoneDescription] = useState("");
  const [savingTime, setSavingTime] = useState(false);
  // 完了作業削除
  const [deleteDoneConfirmOpen, setDeleteDoneConfirmOpen] = useState(false);
  const [deletingDone, setDeletingDone] = useState(false);

  const backUrl = `/workers/${workerId}/workflows/${workflowId}`;
  const t = (id, values) => intl.formatMessage({ id }, values);

  // ── 作業詳細読み込み ──
  const loadWork = useCallback(async () => {
    try {
      const data = await fetchWorkDetail(workId);
      // work.applicationUrl / personUrl が未設定の場合は Action から補完する
      if (data && data.actionId && (!data.applicationUrl || !data.personUrl)) {
        try {
          const action = await fetchAction(data.actionId);
          if (action?.applicationUrl) data.applicationUrl = action.applicationUrl;
          if (action?.personUrl) data.personUrl = action.personUrl;
        } catch {
          // URL 情報なしで続行
        }
      }
      setWork(data);
      if (data?.description) {
        setWorkResult(data.description);
      } else if (data?.workDescription && passedStatus === "Doing") {
        // Doing 状態で結果未記入の場合、作業説明を初期値として設定
        setWorkResult(data.workDescription + "\n\n");
      }
    } catch {
      // passedWork をフォールバックとして使用
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => { loadWork(); }, [loadWork]);

  // ナレッジエージェント
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // タブごとのロード済みフラグ（Done 初期タブがアウトプット=2 なので outputLoaded も初期化）
  const [guidelinesLoaded, setGuidelinesLoaded] = useState(false);
  const [inputLoaded, setInputLoaded] = useState(false);
  const [outputLoaded, setOutputLoaded] = useState(false);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  // Done で初期タブ=2 のときマウント時にアウトプット・評価をロードする
  useEffect(() => {
    if (passedStatus === "Done") {
      setOutputLoaded(true);
      loadOutputData();
      loadEvaluations();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── タブ切り替え時にデータ取得 ──
  useEffect(() => {
    if (tabIndex === 0 && !guidelinesLoaded) {
      setGuidelinesLoading(true);
      fetchWorkGuidelines(workId)
        .then((data) => setGuidelines(Array.isArray(data) ? data : []))
        .catch((err) => { notify.apiError(err); setGuidelines([]); })
        .finally(() => { setGuidelinesLoading(false); setGuidelinesLoaded(true); });
    }
    if (tabIndex === 1 && !inputLoaded) {
      setInputLoading(true);
      fetchWorkInputDataCategories(workId)
        .then((data) => setInputCategories(Array.isArray(data) ? data : []))
        .catch((err) => { notify.apiError(err); setInputCategories([]); })
        .finally(() => { setInputLoading(false); setInputLoaded(true); });
    }
    if (tabIndex === 2 && !outputLoaded) {
      setOutputLoaded(true);
      loadOutputData();
    }
    if (tabIndex === 3 && !agentsLoaded) {
      setAgentsLoading(true);
      fetchAgentsByWork(workId)
        .then((data) => setAgents(Array.isArray(data) ? data : []))
        .catch((err) => { notify.apiError(err); setAgents([]); })
        .finally(() => { setAgentsLoading(false); setAgentsLoaded(true); });
    }
  }, [tabIndex]); // eslint-disable-line

  const loadOutputData = useCallback(async () => {
    setOutputLoading(true);
    try {
      const [data, cats] = await Promise.all([
        fetchWorkOutputData(workId),
        fetchWorkOutputDataCategories(workId),
      ]);
      setOutputData(Array.isArray(data) ? data : []);
      setOutputCategories(Array.isArray(cats) ? cats : []);
    } catch {
      setOutputData([]);
    } finally {
      setOutputLoading(false);
    }
  }, [workId]);

  // ── 評価一覧ロード ──
  const loadEvaluations = useCallback(async () => {
    if (!workId) return;
    setEvaluationsLoading(true);
    try {
      const data = await fetchEvaluations(workId);
      setEvaluations(Array.isArray(data) ? data : []);
    } catch {
      setEvaluations([]);
    } finally {
      setEvaluationsLoading(false);
    }
  }, [workId]);

  // ── 評価フォームを開く ──
  const openEvalDialog = (evalObj = null) => {
    setEvalEditTarget(evalObj);
    setEvalName(evalObj?.evaluationName || "");
    setEvalDescription(evalObj?.evaluationDescription || "");
    setEvalScore(evalObj?.evaluationNumber || 3);
    setEvalDialogOpen(true);
  };

  // ── 評価保存（新規 or 編集） ──
  const handleEvalSave = async () => {
    if (!evalName.trim() || !evalDescription.trim()) return;
    try {
      if (evalEditTarget) {
        await updateEvaluation({
          ...evalEditTarget,
          evaluatorId: memberId,        // バックエンド検証用
          evaluationName: evalName.trim(),
          evaluationDescription: evalDescription.trim(),
          evaluationNumber: evalScore,
        });
        notify.success(t("work.evaluation.updateSuccess"));
      } else {
        await createEvaluation({
          companyId,
          evaluationTarget: "Work",     // バックエンド enum: EvaluationTarget.Work
          targetId: workId,
          evaluationName: evalName.trim(),
          evaluationDescription: evalDescription.trim(),
          evaluationNumber: evalScore,
          evaluator: memberId,          // エンティティ保存用
          evaluatorId: memberId,        // バックエンド検証用（必須）
        });
        notify.success(t("work.evaluation.createSuccess"));
      }
      setEvalDialogOpen(false);
      loadEvaluations();
    } catch (err) {
      notify.apiError(err);
    }
  };

  // ── 評価削除 ──
  const handleEvalDelete = async (evaluationId) => {
    try {
      await deleteEvaluation(evaluationId);
      notify.success(t("work.evaluation.deleteSuccess"));
      setEvalDeleteId(null);
      setEvaluations((prev) => prev.filter((e) => e.evaluationId !== evaluationId));
    } catch (err) {
      notify.apiError(err);
    }
  };

  // ── 評価完了（平均スコアを works.evaluation_number に保存） ──
  const handleCompleteEvaluation = async () => {
    if (!averageScore) return;
    setCompleting(true);
    try {
      await evaluateWork({
        workId,
        companyId,
        endDate: work.endDate || new Date().toISOString(),
        evaluationNumber: String(Math.round(averageScore * 10) / 10),
      });
      notify.success(t("work.evaluation.completeSuccess"));
      // workType が Evaluated に変わったことを反映するためワーク情報を再取得
      const updated = await fetchWorkDetail(workId);
      setWork(updated);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setCompleting(false);
    }
  };

  // ── 完了作業編集ダイアログを開く ──
  const openEditTimeDialog = () => {
    setEditStartDate(work.startDate ? toLocalDateTimeInput(new Date(work.startDate)) : toLocalDateTimeInput(new Date()));
    setEditEndDate(work.endDate ? toLocalDateTimeInput(new Date(work.endDate)) : toLocalDateTimeInput(new Date()));
    setEditDoneWorkName(work.workName || "");
    setEditDoneDescription(work.workDescription || "");
    setEditTimeOpen(true);
  };

  // ── ToDo ワーク編集 ──
  const handleTodoEditStart = () => {
    setEditTodoName(work.workName || "");
    setEditTodoDescription(work.workDescription || "");
    setTodoEditMode(true);
  };

  const handleTodoSave = async () => {
    setSavingTodo(true);
    const now = new Date().toISOString();
    try {
      await updateTodoWork({
        workId: work.workId,
        workName: editTodoName,
        workDescription: editTodoDescription,
        startDate: work.startDate || now,
        endDate: work.endDate || now,
      });
      notify.success(t("message.update.success"));
      setTodoEditMode(false);
      await loadWork();
    } catch (e) { notify.apiError(e); }
    finally { setSavingTodo(false); }
  };

  const handleTodoDelete = async () => {
    setDeletingTodo(true);
    try {
      await deleteTodoWork(work.workId);
      notify.success(t("message.delete.success"));
      navigate(backUrl);
    } catch (e) { notify.apiError(e); }
    finally { setDeletingTodo(false); setDeleteTodoConfirmOpen(false); }
  };

  // ── 完了作業の保存 ──
  const handleSaveTime = async () => {
    setSavingTime(true);
    try {
      await updateDoneWork({
        workId: work.workId,
        startDate: new Date(editStartDate).toISOString(),
        endDate: new Date(editEndDate).toISOString(),
        workName: editDoneWorkName || work.workName,
        workDescription: editDoneDescription,
      });
      notify.success(t("work.editTime.success"));
      setEditTimeOpen(false);
      loadWork();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSavingTime(false);
    }
  };

  // ── 完了作業の削除 ──
  const handleDeleteDone = async () => {
    setDeletingDone(true);
    try {
      await deleteDoneWork(work.workId);
      notify.success(t("message.delete.success"));
      navigate(backUrl);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setDeletingDone(false);
      setDeleteDoneConfirmOpen(false);
    }
  };

  // ── 作業開始（ToDo → Doing） ──
  const handleStart = async () => {
    if (!work) return;
    setSubmitting(true);
    try {
      await startWork({
        workId: work.workId,
        companyId,
        actionId: work.actionId || "",
        memberId: work.memberId || memberId,
        memberName: work.memberName || buildMemberName(member),
        workflowId,
        workName: work.workName,
        applicationUrl: work.applicationUrl || "",
        personUrl: work.personUrl || "",
        workDescription: work.workDescription || "",
        startDate: new Date(startDate).toISOString(),
      });
      notify.success(t("work.startSuccess"));
      navigate(backUrl, { replace: true });
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── 作業完了（Doing → Done） ──
  const handleComplete = async () => {
    if (!work) return;
    setSubmitting(true);
    try {
      await completeWork({
        workId: work.workId,
        // endDate が手動設定されていればその値、未設定なら完了ボタン押下時刻
        endDate: endDate ? new Date(endDate).toISOString() : new Date().toISOString(),
        companyId,
        description: workResult,
      });
      notify.success(t("work.completeSuccess"));
      navigate(backUrl, { replace: true });
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── アウトプットデータ 保存 ──
  const handleOutputSave = async (formData) => {
    try {
      if (editingOutput) {
        await updateOutputData({ ...formData, dataId: editingOutput.dataId });
        notify.success(t("work.output.updateSuccess"));
      } else {
        await createOutputData(formData);
        notify.success(t("work.output.createSuccess"));
      }
      setOutputDialogOpen(false);
      setEditingOutput(null);
      setOutputData([]); // リセットして再取得
      loadOutputData();
    } catch (err) {
      notify.apiError(err);
    }
  };

  // ── アウトプットデータ 削除 ──
  const handleOutputDelete = async (dataId) => {
    try {
      await deleteOutputData(dataId);
      notify.success(t("work.output.deleteSuccess"));
      setDeleteConfirmId(null);
      setOutputData((prev) => prev.filter((d) => d.dataId !== dataId));
    } catch (err) {
      notify.apiError(err);
    }
  };

  // ── ローディング ──
  if (loading && !work) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!work) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backUrl)} sx={{ mb: 2 }}>
          {t("common.back")}
        </Button>
        <Typography color="text.secondary">{t("work.notFound")}</Typography>
      </Box>
    );
  }

  const expendedTime =
    work.startDate && work.endDate ? calcExpendedTime(work.startDate, work.endDate) : null;
  const resultText = work.description || work.workResult || workResult || "";
  // 評価集計
  const myEvaluation = evaluations.find((e) => e.evaluator === memberId);
  const averageScore = evaluations.length > 0
    ? evaluations.reduce((sum, e) => sum + (e.evaluationNumber || 0), 0) / evaluations.length
    : null;
  const isSelf = memberId === work.memberId;

  return (
    <Box>
      {/* ── ヘッダー ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backUrl)} variant="text">
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {work.workName}
        </Typography>
        <StatusChip status={passedStatus} intl={intl} />
      </Box>

      {/* ── タブ: ガイドライン / インプット / アウトプット ── */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: "1px solid", borderColor: "divider", px: 2 }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            icon={<MenuBookIcon fontSize="small" />}
            iconPosition="start"
            label={t("work.tab.guideline")}
            sx={{ minHeight: 48, textTransform: "none", fontSize: "0.875rem" }}
          />
          <Tab
            icon={<InputIcon fontSize="small" />}
            iconPosition="start"
            label={t("work.tab.input")}
            sx={{ minHeight: 48, textTransform: "none", fontSize: "0.875rem" }}
          />
          <Tab
            icon={<ArticleIcon fontSize="small" />}
            iconPosition="start"
            label={t("work.tab.output")}
            sx={{ minHeight: 48, textTransform: "none", fontSize: "0.875rem" }}
          />
          <Tab
            icon={<SmartToyIcon fontSize="small" />}
            iconPosition="start"
            label={t("work.tab.agent")}
            sx={{ minHeight: 48, textTransform: "none", fontSize: "0.875rem" }}
          />
        </Tabs>

        <Box sx={{ px: 3, pb: 3 }}>
          {/* ─── ガイドライン タブ ─── */}
          <TabPanel value={tabIndex} index={0}>
            {guidelinesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : guidelines.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("work.guideline.empty")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {guidelines.map((g, i) => (
                  <Accordion key={g.guidelineId || i} disableGutters elevation={0}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MenuBookIcon fontSize="small" color="primary" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {g.guidelineName}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {g.guidelineDescription && (
                        <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
                          {g.guidelineDescription}
                        </Typography>
                      )}
                      {g.url && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          component={Link}
                          href={g.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("work.input.open")}
                        </Button>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* ─── インプット タブ ─── */}
          <TabPanel value={tabIndex} index={1}>
            {inputLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : inputCategories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("work.input.empty")}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {inputCategories.map((cat, i) => (
                  <Card key={cat.dataCategoryId || i} variant="outlined">
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                        <FolderIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {cat.dataCategoryName}
                          </Typography>
                          {(cat.description || cat.dataCategoryDescription) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              {cat.description || cat.dataCategoryDescription}
                            </Typography>
                          )}
                          {cat.url && (
                            <Box sx={{ mt: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                component={Link}
                                href={cat.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("work.input.open")}
                              </Button>
                            </Box>
                          )}
                          <DataItemsSection category={cat} t={t} />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* ─── アウトプット タブ ─── */}
          <TabPanel value={tabIndex} index={2}>
            {/* Doing の場合はワーク結果テキストエリアも表示 */}
            {passedStatus === "Doing" && (
              <Box sx={{ mb: 3 }}>
                {work?.workDescription && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1, borderLeft: "3px solid", borderColor: "primary.main" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                      {t("work.description")}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
                      {work.workDescription}
                    </Typography>
                  </Box>
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t("work.result")}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {t("work.result.hint")}
                </Typography>
                <TextField
                  placeholder={t("work.result.placeholder")}
                  value={workResult}
                  onChange={(e) => setWorkResult(e.target.value)}
                  multiline
                  rows={4}
                  size="small"
                  fullWidth
                />
              </Box>
            )}
            {passedStatus !== "Doing" && resultText && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("work.result")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
                  {resultText}
                </Typography>
              </Box>
            )}

            {/* Doing 状態：アウトプットデータカテゴリ一覧（参考情報） */}
            {passedStatus === "Doing" && outputCategories.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("work.output.categories.reference")}
                </Typography>
                <Stack spacing={1}>
                  {outputCategories.map((cat) => (
                    <Card key={cat.dataCategoryId} variant="outlined" sx={{ bgcolor: "grey.50" }}>
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <FolderIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {cat.dataCategoryName}
                            </Typography>
                            {(cat.description || cat.dataCategoryDescription) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                {cat.description || cat.dataCategoryDescription}
                              </Typography>
                            )}
                            <DataItemsSection category={cat} t={t} />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}

            {/* アウトプットデータ一覧 */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("work.output.data.title")}
              </Typography>
              {passedStatus === "Doing" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setEditingOutput(null); setOutputDialogOpen(true); }}
                >
                  {t("work.output.add.button")}
                </Button>
              )}
            </Box>

            {outputLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : outputData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("work.output.empty")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {outputData.map((d, i) => (
                  <Card key={d.dataId || i} variant="outlined">
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <ArticleIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {d.dataName}
                          </Typography>
                          {d.dataCategoryName && (
                            <Chip label={d.dataCategoryName} size="small" variant="outlined"
                              sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }} />
                          )}
                          {d.dataDescription && (
                            <Typography variant="caption" color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}>
                              {d.dataDescription}
                            </Typography>
                          )}
                          {d.url && (
                            <Box sx={{ mt: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                component={Link}
                                href={d.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("work.input.open")}
                              </Button>
                            </Box>
                          )}
                          {/* ── データ項目アコーディオン ── */}
                          {d.dataCategoryId && (() => {
                            const cat = outputCategories.find((c) => c.dataCategoryId === d.dataCategoryId);
                            return cat ? <DataItemsSection category={cat} t={t} /> : null;
                          })()}
                        </Box>
                        {passedStatus === "Doing" && (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title={t("common.edit")}>
                              <IconButton size="small" onClick={() => {
                                setEditingOutput(d);
                                setOutputDialogOpen(true);
                              }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common.delete")}>
                              <IconButton size="small" color="error"
                                onClick={() => setDeleteConfirmId(d.dataId)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* ─── ナレッジエージェント タブ ─── */}
          <TabPanel value={tabIndex} index={3}>
            {agentsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : agents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("work.agent.empty")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {agents.map((ag, i) => (
                  <Accordion key={ag.guidelineId || i} disableGutters elevation={0}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <SmartToyIcon fontSize="small" color="primary" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ag.guidelineName}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {ag.guidelineDescription && (
                        <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: "pre-wrap", lineHeight: 1.8, color: "text.secondary" }}>
                          {ag.guidelineDescription}
                        </Typography>
                      )}
                      {ag.url && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          component={Link}
                          href={ag.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("work.agent.open")}
                        </Button>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* ── 作業情報（コンパクト） ── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
          {t("work.detail.title")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <DetailRow label={t("work.name")} value={work.workName} />
        <DetailRow label={t("work.member")} value={work.memberName} />
        <DetailRow label={t("work.worker")} value={work.workerName} />
        <Box sx={{ py: 1 }}>
          <Typography variant="caption" color="text.secondary">{t("work.description")}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8, mt: 0.5 }}>
            {work.workDescription || "—"}
          </Typography>
        </Box>
        {passedStatus !== "Doing" && resultText && (
          <Box sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">{t("work.result")}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8, mt: 0.5 }}>
              {resultText}
            </Typography>
          </Box>
        )}
        {work.startDate && (
          <DetailRow label={t("work.startDate")} value={new Date(work.startDate).toLocaleString()} />
        )}
        {work.endDate && (
          <DetailRow label={t("work.endDate")} value={new Date(work.endDate).toLocaleString()} />
        )}
        {expendedTime && (
          <DetailRow
            label={t("work.expendedTime")}
            value={t("work.expendedTime.value", {
              days: expendedTime.days,
              hours: expendedTime.hours,
              minutes: expendedTime.minutes,
            })}
          />
        )}
        {(passedStatus === "Done" || passedStatus === "Evaluated") && work?.workType !== "Evaluated" && isSelf && (
          <Box sx={{ pt: 1.5 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={openEditTimeDialog}
              >
                {t("work.editDone.button")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDoneConfirmOpen(true)}
              >
                {t("common.delete")}
              </Button>
            </Stack>
          </Box>
        )}
        {/* 平均スコアは評価セクションへ移動 */}
        {(work.applicationUrl || work.personUrl) && (
          <Box sx={{ pt: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {work.applicationUrl && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  href={work.applicationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("work.input.applicationUrl")}
                </Button>
              )}
              {work.personUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  href={work.personUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("work.input.personUrl")}
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* ── ToDo: 編集・削除・作業開始フォーム ── */}
      {passedStatus === "Todo" && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {/* 編集・削除ボタン */}
          {!todoEditMode && (
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleTodoEditStart}
              >
                {t("common.edit")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteTodoConfirmOpen(true)}
              >
                {t("common.delete")}
              </Button>
            </Box>
          )}

          {/* 編集フォーム */}
          {todoEditMode && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("common.edit")}
              </Typography>
              <TextField
                label={t("work.name")}
                value={editTodoName}
                onChange={(e) => setEditTodoName(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label={t("work.description")}
                value={editTodoDescription}
                onChange={(e) => setEditTodoDescription(e.target.value)}
                multiline
                rows={4}
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleTodoSave}
                  disabled={savingTodo}
                >
                  {savingTodo ? <CircularProgress size={18} color="inherit" /> : t("common.save")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setTodoEditMode(false)}
                  disabled={savingTodo}
                >
                  {t("common.cancel")}
                </Button>
              </Box>
            </Stack>
          )}

          {/* 作業開始フォーム */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t("work.start.title")}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label={t("work.startDate")}
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ maxWidth: 320 }}
            />
            <Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={handleStart}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : t("work.start")}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* ── ToDo削除確認ダイアログ ── */}
      <Dialog open={deleteTodoConfirmOpen} onClose={() => setDeleteTodoConfirmOpen(false)}>
        <DialogTitle>{t("work.deleteConfirm.title")}</DialogTitle>
        <DialogContent>{t("message.delete.confirm")}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTodoConfirmOpen(false)} disabled={deletingTodo}>
            {t("common.cancel")}
          </Button>
          <Button color="error" onClick={handleTodoDelete} disabled={deletingTodo}>
            {deletingTodo ? <CircularProgress size={18} color="inherit" /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Doing: 作業完了フォーム ── */}
      {passedStatus === "Doing" && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t("work.complete.title")}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label={t("work.endDate")}
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ maxWidth: 320 }}
              helperText={t("work.endDate.hint")}
            />
            <Typography variant="caption" color="text.secondary">
              {t("work.complete.outputHint")}
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={handleComplete}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : t("work.complete")}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* ── Done: 評価セクション ── */}
      {passedStatus === "Done" && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {/* ヘッダー */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("work.evaluate.title")}
            </Typography>
            {!isSelf && !myEvaluation && work.workType !== "Evaluated" && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<StarIcon />}
                onClick={() => openEvalDialog()}
              >
                {t("work.evaluation.add")}
              </Button>
            )}
          </Box>

          {/* 平均スコア */}
          {averageScore !== null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2,
              p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
              <StarIcon color="warning" fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("work.averageScore")}
              </Typography>
              <Rating value={averageScore} precision={0.5} readOnly size="small" />
              <Typography variant="body2" color="text.secondary">
                {averageScore.toFixed(1)} / 5
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({t("work.evaluation.count", { count: evaluations.length })})
              </Typography>
            </Box>
          )}

          {/* 自己評価不可メッセージ + 評価完了ボタン */}
          {isSelf && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {t("work.cannotSelfEvaluate")}
              </Typography>
              {evaluations.length > 0 && work.workType !== "Evaluated" && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={handleCompleteEvaluation}
                  disabled={completing}
                >
                  {completing ? t("common.loading") : t("work.evaluation.complete")}
                </Button>
              )}
            </Box>
          )}

          {/* 評価一覧 */}
          {evaluationsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : evaluations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("work.evaluation.empty")}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {evaluations.map((ev) => {
                const isMyEval = ev.evaluator === memberId;
                return (
                  <Card key={ev.evaluationId} variant="outlined"
                    sx={{ bgcolor: isMyEval ? "secondary.50" : "inherit" }}>
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {ev.evaluationName}
                            </Typography>
                            {isMyEval && (
                              <Chip label={t("work.evaluation.mine")} size="small"
                                color="secondary" variant="outlined"
                                sx={{ height: 18, fontSize: "0.65rem" }} />
                            )}
                          </Box>
                          <Rating value={ev.evaluationNumber} readOnly size="small" sx={{ mb: 0.5 }} />
                          {ev.evaluationDescription && (
                            <Typography variant="body2" color="text.secondary"
                              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                              {ev.evaluationDescription}
                            </Typography>
                          )}
                        </Box>
                        {isMyEval && (
                          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                            <Tooltip title={t("common.edit")}>
                              <IconButton size="small" onClick={() => openEvalDialog(ev)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common.delete")}>
                              <IconButton size="small" color="error"
                                onClick={() => setEvalDeleteId(ev.evaluationId)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* ── 評価 追加/編集ダイアログ ── */}
      <Dialog open={evalDialogOpen} onClose={() => setEvalDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
          {evalEditTarget ? t("work.evaluation.edit.title") : t("work.evaluation.add.title")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t("work.evaluation.name")}
              value={evalName}
              onChange={(e) => setEvalName(e.target.value)}
              size="small"
              fullWidth
              required
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t("work.evaluateScore")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Rating
                  value={evalScore}
                  onChange={(_, val) => setEvalScore(val || 1)}
                  max={5}
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarIcon fontSize="inherit" />}
                />
                <Typography variant="body2" color="text.secondary">({evalScore} / 5)</Typography>
              </Box>
            </Box>
            <TextField
              label={t("work.evaluation.description")}
              value={evalDescription}
              onChange={(e) => setEvalDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={4}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvalDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="secondary"
            onClick={handleEvalSave}
            disabled={!evalName.trim() || !evalDescription.trim()}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 評価削除確認ダイアログ ── */}
      <Dialog open={!!evalDeleteId} onClose={() => setEvalDeleteId(null)} PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("work.evaluation.deleteConfirm.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("work.evaluation.deleteConfirm.message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvalDeleteId(null)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error"
            onClick={() => handleEvalDelete(evalDeleteId)}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── アウトプットデータ 追加/編集ダイアログ ── */}
      <OutputDataFormDialog
        open={outputDialogOpen}
        onClose={() => { setOutputDialogOpen(false); setEditingOutput(null); }}
        onSave={handleOutputSave}
        editData={editingOutput}
        outputCategories={outputCategories}
        workId={workId}
        companyId={companyId}
        intl={intl}
      />

      {/* ── アウトプットデータ 削除確認ダイアログ ── */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("work.output.deleteConfirm.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("work.output.deleteConfirm.message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error"
            onClick={() => handleOutputDelete(deleteConfirmId)}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 完了作業編集ダイアログ ── */}
      <Dialog open={editTimeOpen} onClose={() => setEditTimeOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("work.editDone.title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t("work.name")}
              value={editDoneWorkName}
              onChange={(e) => setEditDoneWorkName(e.target.value)}
              size="small"
              fullWidth
              required
            />
            <TextField
              label={t("work.description")}
              value={editDoneDescription}
              onChange={(e) => setEditDoneDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={4}
            />
            <TextField
              label={t("work.startDate")}
              type="datetime-local"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label={t("work.endDate")}
              type="datetime-local"
              value={editEndDate}
              onChange={(e) => setEditEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTimeOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSaveTime}
            disabled={savingTime || !editStartDate || !editEndDate || !editDoneWorkName}
          >
            {savingTime ? <CircularProgress size={20} color="inherit" /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 完了作業削除確認ダイアログ ── */}
      <Dialog open={deleteDoneConfirmOpen} onClose={() => setDeleteDoneConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("work.deleteDone.confirm.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("work.deleteConfirm.message", { workName: work?.workName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDoneConfirmOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteDone}
            disabled={deletingDone}
          >
            {deletingDone ? <CircularProgress size={20} color="inherit" /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
